// packages/web/src/lib/progress-sync-hook.ts
/**
 * makeProgressSyncHook — build the post-write hook that enqueues learning-progress
 * field-level events for cloud sync.
 *
 * 19BO+ Line 2 B4.4 step 4: the activation point. The hook is registered via
 * setProgressSyncHook (idb.ts) so that every updateProgress write fires it. The
 * hook diffs before→after, mints client-side eventIds, and enqueues a single
 * batch entry to STORE_SYNC_QUEUE — the drain loop (use-sync-engine.ts) ships
 * it to POST /sync/progress/:courseId on the next 30s tick (or immediately on
 * Ctrl+S / online event).
 *
 * Two gates (both must pass for the hook to enqueue):
 *   1. userId is non-null (anonymous = local-only, never cloud-synced).
 *   2. The caller (App-level opt-in gate) only registers the hook when
 *      progressCloudSync preference is on. When off, setProgressSyncHook(null)
 *      makes updateProgress skip even the structuredClone snapshot.
 *
 * Why a factory (not a plain function): the hook needs the current userId at
 * *call* time (not registration time), because registration happens once at app
 * mount but the user can log in/out later. The factory takes a `userId` getter
 * closure; the App gate rebuilds the hook when userId changes.
 *
 * Extracted from tests/hooks/progress-sync-hook.test.ts (step 3 captured the
 * contract there). This is the production home — the test now imports from here.
 *
 * Spec: docs/architecture/llm-native.md §13.4
 */

import type { CourseProgress } from '@examforge/shared/schemas';
import { diffProgress, toUploadEvents } from './progress-event-emitter.js';
import { enqueueSync } from '../storage/idb.js';
import { useStore } from './store/index.js';

/**
 * Build the hook. `getUserId` is called on every updateProgress write so the
 * hook always sees the current auth state.
 */
export function makeProgressSyncHook(getUserId: () => string | null) {
  return (courseId: string, before: CourseProgress, after: CourseProgress): void => {
    const userId = getUserId();
    if (!userId) return; // anonymous — local only
    const occurredAt = Date.now();
    const emitted = diffProgress(courseId, before, after, occurredAt);
    if (emitted.length === 0) return;
    const events = toUploadEvents(emitted, userId, courseId);
    // Enqueue as a single batch entry (drain loop sends one POST per entry).
    void enqueueSync({ type: 'progress', courseId, payload: events });
    // B5.4: flip the sync-status indicator to 'pending' so the user sees their
    // write is queued for upload. Uses getState() (not the hook) because this is
    // a plain function called from idb's updateProgress, not a React component.
    // Harmless for anon users — the drain debounce effect re-checks isAuthed.
    useStore.getState().markPending();
  };
}
